import { useCallback, useMemo, useRef, useState } from "react";
import { ApolloError } from "@apollo/client";
import { toast } from "@jsq/ds/core";
import { addAction } from "@jsq/observability";
import {
  PackageDetailDocument,
  useSubmitFindingFeedbackMutation,
} from "#graphql/supergraphHooks";
import { FAY_RUM_ACTIONS } from "../constants";
import { isFindingGoneError, isStaleFindingError } from "../utils/feedbackErrors";
import { useRequiredArenaId } from "./useRequiredArenaId";
import type { FeedbackRating, PackageDetailQuery } from "#graphql/supergraphTypes";

export type UseSubmitFindingFeedback = {
  /** Toggles one finding's thumbs-down feedback: `rating: "DOWN"` flags it (with
   *  an optional note), `rating: "NONE"` clears it. Patches the `PackageDetail`
   *  cache from the response so the row renders the new `check.feedback` without
   *  a refetch. */
  submit: (findingId: string, rating: FeedbackRating, note?: string | null) => Promise<void>;
  /** Whether a feedback write is currently in flight for the given finding. */
  isPending: (findingId: string) => boolean;
};

/**
 * Wraps `submitFindingFeedback`. One mutation instance serves the whole Checks
 * tab; `pendingFindingIds` tracks which row is in flight so only that row's
 * controls disable, mirroring `useTriageFinding`'s set-based pending tracking (a
 * single string would let a resolved row on one finding clear the flag while
 * another finding's write is still running).
 *
 * On success, patches the matching check's `feedback` field directly in the
 * `PackageDetail` query cache from `result.submitFindingFeedback.feedback`
 * (null on clear) — no refetch — and toasts success on the flag (DOWN) path.
 * On `STALE_FINDING` / `NOT_FOUND` (typed `extensions.code`, not
 * message-substring matching — see `feedbackErrors.ts`) or any other failure,
 * toasts and refetches so the row resyncs from source.
 *
 * @param arenaId Write-path arena id. Writes fire on user action, well after the
 * host app's arena resolves, so an absent id here is a programming error that
 * throws rather than silently no-ops.
 */
export function useSubmitFindingFeedback(
  refetch: () => void,
  packageId: string | null,
  arenaId: number | undefined,
): UseSubmitFindingFeedback {
  const getArenaId = useRequiredArenaId(arenaId);
  const [mutate] = useSubmitFindingFeedbackMutation();
  const [pendingFindingIds, setPendingFindingIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // Synchronous in-flight guard. `pendingFindingIds` is a boolean-per-id (a
  // Set), so it can't represent two concurrent writes for the same finding, and
  // the row's disabled/isLoading gating is async React state a sub-frame
  // double-click can beat. A ref updates immediately, so we dedupe here: a
  // finding has at most one write in flight, which also keeps `pendingFindingIds`
  // accurate (no early re-enable) and prevents DOWN/NONE from racing each other
  // for the same finding.
  const inFlightFindingIds = useRef<Set<string>>(new Set());

  const submit = useCallback(
    async (findingId: string, rating: FeedbackRating, note?: string | null): Promise<void> => {
      if (packageId === null) {
        return;
      }
      if (inFlightFindingIds.current.has(findingId)) {
        return;
      }
      const resolvedArenaId = getArenaId();
      inFlightFindingIds.current.add(findingId);
      setPendingFindingIds((prev) => {
        const next = new Set(prev);
        next.add(findingId);
        return next;
      });
      // An empty / whitespace-only note is stored as null, not "" — a
      // thumbs-down with no note has no note (the server also strips, but the
      // wire value should already be canonical).
      const normalizedNote = note?.trim() ? note : null;
      try {
        await mutate({
          variables: {
            input: { findingId, rating, note: normalizedNote, arenaId: resolvedArenaId },
          },
          update: (cache, { data }) => {
            if (data?.submitFindingFeedback == null) {
              return;
            }
            const newFeedback = data.submitFindingFeedback.feedback ?? null;
            cache.updateQuery<PackageDetailQuery>(
              {
                query: PackageDetailDocument,
                variables: { arenaId: resolvedArenaId, id: packageId },
              },
              (existing) => {
                if (!existing?.package) {
                  return existing;
                }
                return {
                  ...existing,
                  package: {
                    ...existing.package,
                    documents: existing.package.documents.map((doc) => ({
                      ...doc,
                      sections: doc.sections.map((section) => ({
                        ...section,
                        checks: section.checks.map((check) =>
                          check.findingId === findingId
                            ? { ...check, feedback: newFeedback }
                            : check,
                        ),
                      })),
                    })),
                  },
                };
              },
            );
          },
        });
        // Numerator of the finding *dispute* rate, not the engagement rate:
        // FeedbackRating has no positive value, so this only ever records a
        // complaint. `useTriageFinding` carries the engagement numerator. Both
        // divide by the findings count ChecksTab reports.
        //
        // A RUM action rather than the backend's submit metric so both halves
        // come from the same sampled population — RUM samples sessions at 40%
        // in production, the backend traces at 100%, and dividing across the
        // two would inflate the rate. The rating rides along so the dashboard
        // can drop `NONE`, which clears a flag rather than raising one.
        addAction(FAY_RUM_ACTIONS.findingFeedbackSubmitted, {
          rating,
          package_id: packageId,
        });
        // Confirm the write landed. Only the flag (DOWN) path toasts success —
        // clearing (NONE) is already confirmed via the ClearFeedbackModal and
        // the row visibly un-flags, so a second toast there would be noise.
        if (rating === "DOWN") {
          toast.success("Feedback submitted successfully.");
        }
      } catch (error) {
        // A typed conflict (the finding moved or its document was removed
        // underneath the reviewer) isn't a retriable failure — refetch resyncs
        // the row from source, so surface a neutral "refreshing" message rather
        // than an actionable "try again". STALE_FINDING and gone
        // (NOT_FOUND / FINDING_WITHDRAWN) get distinct copy; everything else is
        // a real error the reviewer can retry.
        const apolloError = error instanceof ApolloError ? error : null;
        if (apolloError && isStaleFindingError(apolloError)) {
          toast.error("This check was updated by a newer run. Refreshing…");
        } else if (apolloError && isFindingGoneError(apolloError)) {
          toast.error("This check is no longer available. Refreshing…");
        } else {
          toast.error("Couldn’t save feedback. Please try again.");
        }
        refetch();
      } finally {
        inFlightFindingIds.current.delete(findingId);
        setPendingFindingIds((prev) => {
          const next = new Set(prev);
          next.delete(findingId);
          return next;
        });
      }
    },
    [getArenaId, mutate, packageId, refetch],
  );

  const isPending = useCallback(
    (findingId: string): boolean => pendingFindingIds.has(findingId),
    [pendingFindingIds],
  );

  return useMemo(() => ({ submit, isPending }), [submit, isPending]);
}
